import React, { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { useFetchOrganizationOfCurrentUser } from '../api/hooks/auth';
import { useFetchAgreements, useFetchPatient, useFetchPatients } from '../api/hooks/patient';
import { FeedEvent } from '../socket';
import {
  createPatientStore,
  PatientState,
  PatientStoreApi,
  selectUser,
  selectUsers,
  selectTempPatients,
  selectUserAgreements,
  selectIsStimHomeAgreed,
  selectIsExpiredCommonAgree,
  selectIsStimAgreed,
  selectIsTempUser,
} from '../stores/createPatientStore';

import { useFeedSocketClient } from './FeedSocketProvider';

const TEMP_STORAGE_KEY = 'TEMP_USERS';

const PatientStoreContext = createContext<PatientStoreApi | null>(null);

// 파생 셀렉터 재노출: 소비처는 usePatientStore(selectUser)처럼 필요한 슬라이스만 구독한다.
export {
  selectUser,
  selectUsers,
  selectTempPatients,
  selectUserAgreements,
  selectIsStimHomeAgreed,
  selectIsExpiredCommonAgree,
  selectIsStimAgreed,
  selectIsTempUser,
};

export function usePatientStore<U>(selector: (s: PatientState) => U): U {
  const api = useContext(PatientStoreContext);
  if (!api) {
    throw new Error('usePatientStore must be used within PatientProvider');
  }
  return useStore(api, selector);
}

type PatientSignal = { patientId: string; editInfo?: boolean; deleted?: boolean };

const PatientProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: organization } = useFetchOrganizationOfCurrentUser();
  const orgId = organization?.id;
  // orgId가 async로 resolve(undefined→실값)되면 store를 재생성한다. useMemo는 값 보존이 보장되지
  // 않으므로(React가 메모를 버릴 수 있음), 재생성이 필요한 파생 store는 useState + prev 비교 패턴으로 둔다.
  const [storeApi, setStoreApi] = useState(() => createPatientStore(orgId));
  const [prevOrgId, setPrevOrgId] = useState(orgId);
  if (orgId !== prevOrgId) {
    setPrevOrgId(orgId);
    setStoreApi(createPatientStore(orgId));
  }

  // cross-tab temp user 동기화 (구 생성자 내부 리스너 → cleanup 포함 effect로 이동)
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === TEMP_STORAGE_KEY) {
        storeApi.getState()._setTempUsersFromStorage(event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storeApi]);

  const feedClient = useFeedSocketClient();
  const [updatedUser, setUpdatedUser] = useState<PatientSignal | undefined>();

  useEffect(() => {
    if (!feedClient) return;
    const subId = feedClient.subscribe(
      ['USER_AGREED', 'PATIENT_UPDATED', 'PATIENT_DELETED', 'SURVEY_SESSION_CREATED', 'SURVEY_SESSION_UPDATED', 'SURVEY_SESSION_DELETED'],
      (event: FeedEvent) => {
        if (event.type === 'PATIENT_DELETED') {
          setUpdatedUser({ patientId: event.patientId, deleted: true });
        } else if (event.type === 'PATIENT_UPDATED' || event.type === 'SURVEY_SESSION_DELETED') {
          setUpdatedUser({ patientId: event.patientId, editInfo: true });
        } else {
          setUpdatedUser({ patientId: event.patientId });
        }
      },
    );
    return () => feedClient.unsubscribe(subId);
  }, [feedClient]);

  // 구 observer의 store.user 반응 읽기 대체: user 변경 시 재렌더 → agreements refetch
  const userId = useStore(storeApi, (s) => selectUser(s)?.id);

  const { refetch: refetchPatient } = useFetchPatient(updatedUser?.patientId, false);
  const { data: agreements, refetch: refetchAgreements } = useFetchAgreements(userId);
  const { data: usersData, hasNextPage, fetchNextPage } = useFetchPatients('');

  const handleUpdatePatient = React.useCallback(async () => {
    if (updatedUser?.patientId === userId) {
      refetchAgreements();
    }
    const { data: patient } = await refetchPatient();
    if (patient) {
      storeApi.getState().updateUser(patient, updatedUser?.editInfo || false);
    }
  }, [storeApi, updatedUser, userId, refetchPatient, refetchAgreements]);

  useEffect(() => {
    if (updatedUser?.patientId) {
      if (updatedUser.deleted) {
        storeApi.getState().deleteUser(updatedUser.patientId);
      } else {
        handleUpdatePatient();
      }
    }
  }, [updatedUser, storeApi, handleUpdatePatient]);

  useEffect(() => {
    if (agreements) {
      storeApi.getState().setUserAgreements(agreements.patientId, agreements?.agreements);
    }
  }, [agreements, storeApi]);

  useEffect(() => {
    const users = usersData?.pages || [];
    storeApi.getState().setUsers(users);
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [usersData, storeApi, hasNextPage, fetchNextPage]);

  return <PatientStoreContext.Provider value={storeApi}>{children}</PatientStoreContext.Provider>;
};

export default PatientProvider;
